import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './EventForm.css';

const EventForm = () => {
  const [formData, setFormData] = useState({
    cardNumber: '',
    packId: '',
    price: '',
    packType: '',
    clientName: '',
    clientEmail: '',
    phoneNumber: '',
    location: '',
    validityPeriod: '1 year',
    salesPerson: '',
    childrenCount: 0,
    childrenNames: ['']
  });

  const [locations, setLocations] = useState([]);
  const [packs, setPacks] = useState([]);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardCheckMessage, setCardCheckMessage] = useState('');
  const [isCheckingCard, setIsCheckingCard] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);

  // Charger les localisations disponibles
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get('/api/locations/getAllLocations');
        setLocations(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des localisations:', error);
        setMessage('❌ Impossible de charger les lieux de vente.');
      }
    };

    const fetchPacks = async () => {
      try {
        const response = await axios.get('/api/pack/getAll');
        setPacks(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des packs:', error);
        setMessage('❌ Impossible de charger les packs.');
      }
    };

    fetchLocations();
    fetchPacks();
  }, []);

  // Vérifier si le numéro de carte existe déjà
  const checkCardNumber = useCallback(async (cardNumber) => {
    if (!cardNumber || !/^\d{3}-\d{4}$/.test(cardNumber)) {
      setCardCheckMessage('');
      return;
    }

    setIsCheckingCard(true);
    try {
      const response = await axios.get(`/api/event/check-card/${cardNumber}`);
      setCardCheckMessage(response.data.message);
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);
      setCardCheckMessage('❌ Erreur lors de la vérification du numéro');
    } finally {
      setIsCheckingCard(false);
    }
  }, []);

  const handleCardNumberChange = (e) => {
    let value = e.target.value;
    
    // Supprimer tout sauf les chiffres
    value = value.replace(/[^\d]/g, '');
    
    // Limiter à 7 chiffres maximum (3 pour le code + 4 pour l'année)
    if (value.length > 7) {
      value = value.slice(0, 7);
    }
    
    // Formater en XXX-XXXX
    if (value.length > 3) {
      value = `${value.slice(0, 3)}-${value.slice(3)}`;
    }
    
    setFormData(prevState => ({
      ...prevState,
      cardNumber: value
    }));

    // Vérifier le numéro de carte après un délai
    setTimeout(() => {
      if (value.length === 8) { // XXX-XXXX
        checkCardNumber(value);
      }
    }, 500);
  };

  const handlePackChange = (e) => {
    const packId = e.target.value;
    const pack = packs.find(p => p._id === packId);
    
    setSelectedPack(pack);
    
    if (pack) {
      setFormData(prevState => ({
        ...prevState,
        packId: pack._id,
        price: pack.price,
        packType: pack.name,
        validityPeriod: pack.validityPeriod,
        childrenCount: pack.includesChildren ? pack.maxChildren : 0,
        childrenNames: Array(pack.includesChildren ? pack.maxChildren : 0).fill('')
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        packId: '',
        price: '',
        packType: '',
        childrenCount: 0,
        childrenNames: ['']
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cardNumber') {
      handleCardNumberChange(e);
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleChildNameChange = (index, value) => {
    const newChildrenNames = [...formData.childrenNames];
    newChildrenNames[index] = value;
    
    setFormData(prevState => ({
      ...prevState,
      childrenNames: newChildrenNames
    }));
  };

  const validateForm = () => {
    // Validation du numéro de carte
    const cardNumberRegex = /^\d{3}-\d{4}$/;
    if (!cardNumberRegex.test(formData.cardNumber)) {
      setMessage('❌ Le numéro de carte doit être au format 001-2023 (3 chiffres + 4 chiffres)');
      return false;
    }

    // Validation du pack
    if (!formData.packId) {
      setMessage('❌ Veuillez sélectionner un pack');
      return false;
    }

    // Validation du nom du client
    if (!formData.clientName || formData.clientName.trim().length < 2) {
      setMessage('❌ Le nom du client est requis');
      return false;
    }

    // Validation de l'email
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!formData.clientEmail || !emailRegex.test(formData.clientEmail)) {
      setMessage('❌ Veuillez fournir une adresse email valide');
      return false;
    }

    // Validation du téléphone
    if (!formData.phoneNumber || formData.phoneNumber.replace(/\D/g, '').length < 8) {
      setMessage('❌ Le numéro de téléphone est invalide');
      return false;
    }

    // Validation du lieu
    if (!formData.location) {
      setMessage('❌ Veuillez sélectionner un lieu de vente');
      return false;
    }

    // Validation du commercial
    if (!formData.salesPerson || formData.salesPerson.trim().length < 2) {
      setMessage('❌ Le nom du commercial est requis');
      return false;
    }

    // Validation des noms d'enfants si le pack inclut des enfants
    if (selectedPack && selectedPack.includesChildren) {
      for (let i = 0; i < formData.childrenCount; i++) {
        if (!formData.childrenNames[i] || formData.childrenNames[i].trim().length < 2) {
          setMessage(`❌ Veuillez saisir le nom de l'enfant ${i + 1}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!validateForm()) {
      return;
    }

    // Vérification finale du numéro de carte
    if (cardCheckMessage.includes('déjà utilisé')) {
      setMessage('❌ Ce numéro de carte est déjà utilisé. Veuillez en choisir un autre.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/event/create', formData);
      setMessage('✅ Vente enregistrée avec succès!');
      setCardCheckMessage('');
      
      // Réinitialiser le formulaire
      setFormData({
        cardNumber: '',
        packId: '',
        price: '',
        packType: '',
        clientName: '',
        clientEmail: '',
        phoneNumber: '',
        location: '',
        validityPeriod: '1 year',
        salesPerson: '',
        childrenCount: 0,
        childrenNames: ['']
      });
      setSelectedPack(null);
    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur de connexion au serveur';
      setMessage('❌ ' + errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="subscription-sales-container">
      <div className="subscription-sales-card">
        <h2 className="subscription-sales-title">
          <span className="sales-icon">🎫</span>
          Vente de Carte d'Abonnement
        </h2>
        
        {message && (
          <div className={`subscription-sales-message ${message.includes('❌') ? 'sales-error' : 'sales-success'}`}>
            {message}
          </div>
        )}

        <form className="subscription-sales-form" onSubmit={handleSubmit}>
          {/* Numéro de carte */}
          <div className="sales-form-group">
            <label htmlFor="cardNumber" className="sales-form-label">
              Numéro de Carte *
            </label>
            <input
              type="text"
              id="cardNumber"
              name="cardNumber"
              value={formData.cardNumber}
              onChange={handleChange}
              placeholder="001-2023"
              maxLength="8"
              className="sales-form-input"
              required
            />
            <div className="sales-form-hint">
              Format: 3 chiffres + 4 chiffres pour l'année (ex: 001-2023)
            </div>
            {cardCheckMessage && (
              <div className={`card-check-message ${cardCheckMessage.includes('⚠️') ? 'card-warning' : 'card-available'}`}>
                {isCheckingCard ? '🔍 Vérification...' : cardCheckMessage}
              </div>
            )}
          </div>

          {/* Sélection du pack */}
          <div className="sales-form-group">
            <label htmlFor="packId" className="sales-form-label">
              Pack d'Abonnement *
            </label>
            <select
              id="packId"
              name="packId"
              value={formData.packId}
              onChange={handlePackChange}
              className="sales-form-select"
              required
            >
              <option value="">Sélectionnez un pack</option>
              {packs.map((pack) => (
                <option key={pack._id} value={pack._id}>
                  {pack.name} - {pack.price} DT ({pack.validityPeriod})
                </option>
              ))}
            </select>
            {selectedPack && (
              <div className="pack-description">
                <strong>Description:</strong> {selectedPack.description}
                {selectedPack.includesChildren && (
                  <div>
                    <strong>Enfants inclus:</strong> {selectedPack.maxChildren}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Prix (affichage seulement) */}
          <div className="sales-form-group">
            <label className="sales-form-label">
              Prix (DT)
            </label>
            <input
              type="text"
              value={formData.price ? `${formData.price} DT` : ''}
              className="sales-form-input"
              readOnly
              style={{backgroundColor: '#f8f9fa', color: '#495057'}}
            />
          </div>

          {/* Nom du client */}
          <div className="sales-form-group">
            <label htmlFor="clientName" className="sales-form-label">
              Nom du Client *
            </label>
            <input
              type="text"
              id="clientName"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              className="sales-form-input"
              required
            />
          </div>

          {/* Email du client */}
          <div className="sales-form-group">
            <label htmlFor="clientEmail" className="sales-form-label">
              Email du Client *
            </label>
            <input
              type="email"
              id="clientEmail"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleChange}
              className="sales-form-input"
              placeholder="client@example.com"
              required
            />
          </div>

          {/* Numéro de téléphone */}
          <div className="sales-form-group">
            <label htmlFor="phoneNumber" className="sales-form-label">
              Téléphone *
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="sales-form-input"
              required
            />
          </div>

          {/* Lieu de vente */}
          <div className="sales-form-group">
            <label htmlFor="location" className="sales-form-label">
              Lieu de Vente *
            </label>
            <select
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="sales-form-select"
              required
            >
              <option value="">Sélectionnez un lieu</option>
              {locations.map((location, index) => (
                <option key={index} value={location.name || location._id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* Période de validité (affichage seulement si pack sélectionné) */}
          {selectedPack && (
            <div className="sales-form-group">
              <label className="sales-form-label">
                Période de Validité
              </label>
              <input
                type="text"
                value={formData.validityPeriod}
                className="sales-form-input"
                readOnly
                style={{backgroundColor: '#f8f9fa', color: '#495057'}}
              />
            </div>
          )}

          {/* Noms des enfants (si pack inclut des enfants) */}
          {selectedPack && selectedPack.includesChildren && formData.childrenCount > 0 && (
            <div className="sales-form-group" style={{gridColumn: '1 / -1'}}>
              <label className="sales-form-label">
                Noms des Enfants * ({formData.childrenCount} inclus)
              </label>
              <div className="children-names-container">
                {formData.childrenNames.map((name, index) => (
                  <div key={index} className="child-name-input">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => handleChildNameChange(index, e.target.value)}
                      className="sales-form-input"
                      placeholder={`Nom de l'enfant ${index + 1}`}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nom du commercial */}
          <div className="sales-form-group">
            <label htmlFor="salesPerson" className="sales-form-label">
              Vendu par *
            </label>
            <input
              type="text"
              id="salesPerson"
              name="salesPerson"
              value={formData.salesPerson}
              onChange={handleChange}
              className="sales-form-input"
              placeholder="Nom du commercial"
              required
            />
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting || isCheckingCard}
            className="sales-submit-btn"
          >
            {isSubmitting ? (
              <>
                <span className="sales-spinner"></span>
                Enregistrement...
              </>
            ) : (
              '📋 Enregistrer la Vente'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventForm;